import { z } from 'zod';
import { addressLocationSchema, createModel, regionGeometrySchema } from '../../common';
import { regionTypeSchema } from './region.type.model';
import { regionScoresSchema } from './region.score.model';
import { regionStatusSchema } from './region.status.model';
import { regionHubInfoSchema } from './region.hub.model';
import { regionMetadataSchema } from './region.metadata.model';
import { UTagSharedSchema } from '../utag';

const multiLangStringArraySchema = z.object({
  en: z.array(z.string().trim()).default([]),
  ar: z.array(z.string().trim()).default([]),
  ur: z.array(z.string().trim()).default([]),
});
export type RegionMultiLangAliases = z.infer<typeof multiLangStringArraySchema>;

/**
 * `region db model`
 *
 * The platform's canonical geofence collection. A region can be a country, state
 * (Saudi province / منطقة), governorate (محافظة), city, neighborhood, or hub
 * (port / industrial city / free zone / border crossing).
 *
 * **Hierarchy** is implied by `type` — see `regionTypeSchema`. Parent of any region
 * is resolved at read time by `$geoIntersects` of `center` against the parent type's
 * `boundaries`. There is no stored `parentRegion` ref.
 *
 * **Identifier**: `customCode` is the only identifier. It is always derived from the
 * Google Maps English `long_name` via `makeCustomCode()` in `@hero/core` so importer-
 * seeded docs and runtime-discovered docs merge cleanly on `{ type, customCode }`.
 *
 * **Display**: orders / agreements / routes / invoices store only `customCode`.
 * Display names are resolved at render time in the viewer's language via
 * `useTranslateRegions()` (frontend) or `RegionCacheService.translate()` (backend).
 */
export const RegionSchema = createModel({
  /** Region kind. Implies hierarchy — see `regionTypeSchema`. */
  type: regionTypeSchema,

  /**
   * Workflow status — `active` (Super-Admin reviewed), `needs_review` (auto-created
   * by the Google flow), `unverified` (imported from a low-precision source).
   */
  status: regionStatusSchema,

  /**
   * The **only** identifier. Always derived from the Google Maps English `long_name`
   * via `makeCustomCode()` in `@hero/core` (e.g. `saudi_arabia`, `riyadh_region`,
   * `ad_diriyah`, `al_dwadmy`, `jeddah`, `jeddah_islamic_port`).
   *
   * Excel short codes (`SA`, `RUH`, `JIP`) are intentionally NOT stored — Google's
   * slug is the single source of truth so the importer and the runtime Google
   * reverse-geocode flow produce the same value. Never type this in by hand.
   */
  customCode: z.coerce.string().toLowerCase().trim(),

  /** Canonical English display name (drives `customCode`). */
  displayName: z.coerce.string().trim(),
  /** Arabic display name (Excel `*_name_ar`). Display-only; not unique. */
  displayNameAr: z.string().trim().optional(),
  /** Urdu display name (Excel `*_name_ur`). Display-only; not unique. */
  displayNameUr: z.string().trim().optional(),
  /**
   * Display name per language code (`ar`, `en`, `ur`, and any language a
   * pack adds later). Read before the legacy `displayName*` columns, which
   * stay for older readers. Keys are language codes, not a fixed enum, so a
   * new language needs no schema change.
   */
  names: z.record(z.string(), z.string().trim()).optional(),

  /** Center point (lng, lat). Drives the runtime parent-of-X spatial query. */
  center: addressLocationSchema,

  /**
   * Boundary geometry — `Polygon` (outer ring + optional holes) or `MultiPolygon`
   * (disjoint pieces — governorates, Eastern Province, Makkah, ...). Indexed with
   * `2dsphere` for `$geoIntersects` lookups.
   */
  boundaries: regionGeometrySchema,

  /**
   * Spelling / transliteration variants (Jeddah / Jiddah / جدة / جده).
   * Indexed in Atlas Search per language for AI / NLU resolution.
   */
  aliases: multiLangStringArraySchema.default({ en: [], ar: [], ur: [] }),

  /**
   * Excel-provided search keywords (e.g. `'capital, central region, main hub'`).
   * Indexed per language alongside `aliases`.
   */
  keywords: multiLangStringArraySchema.default({ en: [], ar: [], ur: [] }),

  /**
   * Audit + provenance + type-specific block. Present sparingly on Google-auto-
   * created docs; populated by the Saudi master importer.
   */
  metadata: regionMetadataSchema.optional(),

  /** Hub-specific block (category + hubType + connectsToCountryCustomCode). Present iff `type === 'hub'`. */
  hub: regionHubInfoSchema.optional(),

  /** Operational scoring per region (supply / demand / road quality / prevention time). Owned by the scoring service, not the importer. */
  scores: regionScoresSchema,
  /** User-defined tags. */
  UTags: z.array(UTagSharedSchema),
});

/** `region db model` — the platform-supported regions. See `RegionSchema` JSDoc for the full contract. */
export type Region = z.infer<typeof RegionSchema.model>;
export type RegionData = z.infer<typeof RegionSchema.data>;
export type RegionPartial = z.infer<typeof RegionSchema.partial>;
